<script>
  import { apiRequest } from "../utils/api";
  import { formatDate, formatNumber } from "../utils/format.js";
  import RunTimeline from "./RunTimeline.svelte";
  import RunStopReason from "./RunStopReason.svelte";

  // Read-only view of a TERMINAL (completed/failed) run for the run-history
  // archive. It does a SINGLE messages fetch (a terminal run's timeline is
  // final — no polling) and renders the shared <RunTimeline> plus the run's
  // outcome ("why it ended"). It shows NO Start/Stop and NO Approve/Reject: the
  // ApprovalCard is SUPPRESSED ENTIRELY here (awaitingApproval={false}), so a
  // historical pending gate never renders a card at all.
  export let run; // { id, status, cost, steps, error_message, created_at }

  let messages = [];
  let pendingApproval = null;
  let loading = false;
  let loadError = null;
  let loadedRunId = null;

  // Refetch whenever the selected archived run changes.
  $: if (run && run.id !== loadedRunId) {
    loadTimeline(run.id);
  }

  async function loadTimeline(runId) {
    loadedRunId = runId;
    loading = true;
    loadError = null;
    messages = [];
    pendingApproval = null;
    try {
      const payload = await apiRequest(`/api/agent_runs/${runId}/messages`);
      // Guard against a stale in-flight fetch when the user clicks fast.
      if (runId !== loadedRunId) {
        return;
      }
      // Sort by (conversation_order ?? id) so a delivered steering message keeps
      // its in-position placement (after the tool batch it was enqueued amid),
      // matching the live panel + LLM payload; ordinary messages fall back to id.
      messages = Array.isArray(payload.messages)
        ? [...payload.messages].sort(
            (a, b) => (a.conversation_order ?? a.id) - (b.conversation_order ?? b.id)
          )
        : [];
      // Fetched for completeness, but with awaitingApproval={false} below the
      // card is suppressed entirely — a historical gate renders no ApprovalCard.
      pendingApproval = payload.pending_approval || null;
    } catch (error) {
      if (runId !== loadedRunId) {
        return;
      }
      loadError = error?.message || "Could not load this run's timeline.";
    } finally {
      if (runId === loadedRunId) {
        loading = false;
      }
    }
  }

  function statusBadgeClass(status) {
    if (status === "completed") {
      return "bg-success-bg text-success-text border-success-border/20";
    }
    if (status === "failed") {
      return "bg-danger-bg text-danger-text border-danger-border/20";
    }
    return "bg-neutral-100 text-neutral-600 border-neutral-200";
  }
</script>

<section class="bg-neutral-50 border border-neutral-200 rounded-lg p-4 space-y-3" data-testid="run-archive-view">
  <div class="flex items-center justify-between gap-3 flex-wrap">
    <div class="flex items-center gap-2 flex-wrap">
      <h4 class="text-xs font-bold text-neutral-700 tracking-wider">Past run #{run.id}</h4>
      <span
        class="inline-flex items-center px-2 py-0.5 rounded-full text-[0.62rem] font-bold uppercase tracking-wider border {statusBadgeClass(run.status)}"
        data-testid="archive-run-status"
      >
        {run.status}
      </span>
    </div>
    <span class="text-[0.62rem] text-neutral-400">{formatDate(run.created_at)}</span>
  </div>

  <p class="text-[0.68rem] text-neutral-500" data-testid="archive-read-only-note">
    Read-only history — this run has finished. Controls are disabled.
  </p>

  <!-- Why it ended: prominent for a failed run, muted context for a completed one.
       The failed case renders the SAME <RunStopReason> as the live panel, so
       clicking back into a run cannot downgrade "Stopped by a safety limit — this
       run reached the token ceiling…" back to the bare gem string. -->
  {#if run.error_message}
    <RunStopReason errorMessage={run.error_message} testid="archive-why-ended" />
  {:else if run.status === "completed"}
    <div
      class="bg-success-bg text-success-text border border-success-border/20 rounded px-2.5 py-1.5 text-xs"
      role="status"
      data-testid="archive-why-ended"
    >
      Completed successfully.
    </div>
  {/if}

  {#if loadError}
    <p class="bg-danger-bg text-danger-text border border-danger-border/20 rounded px-2.5 py-1.5 text-xs" role="alert" data-testid="archive-error">
      {loadError}
    </p>
  {/if}

  <div class="space-y-2 max-h-[32rem] overflow-y-auto scroll-smooth" data-testid="run-timeline">
    {#if loading}
      <p class="text-xs text-neutral-500">Loading this run's timeline…</p>
    {:else}
      <!-- awaitingApproval={false} suppresses the ApprovalCard ENTIRELY: a
           historical pending gate renders no card at all (neither the inline nor
           the fallback site fires), so the archive stays purely read-only. -->
      <RunTimeline {messages} awaitingApproval={false} {pendingApproval} />
    {/if}
  </div>
</section>
